/**
 * Attendance page (Salary → Attendance).
 *
 * Reads machine punches synced by the Windows agent and shows, per month:
 *  - Sheet:   staff × day grid (P / L / H / A / PL / O) — click a cell to see
 *             punches and set a manual override (Paid Leave, Present, …).
 *  - Summary: per-staff totals + the exact numbers Payroll Run will pull.
 *  - Mapping: link each Salary Staff to their machine user ID (Ac-No), and
 *             see punches that arrived for IDs nobody is mapped to yet.
 *
 * Rules come from Salary → Attendance Settings. Nothing here modifies payroll.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, orderBy, doc, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { FiRefreshCw, FiX, FiLink, FiAlertCircle, FiInfo, FiDownload } from 'react-icons/fi';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { colors } from '../../theme/colors';
import usePagePermissions from '../../hooks/usePagePermissions';
import {
  loadAttendanceSettings, fetchMonthLogs, fetchMonthAdjustments, buildMonthlySummary,
  STATUS_META, WEEKDAY_NAMES, weekdayOf, pad2, fmt12, fmtHM, minutesOf
} from '../../utils/attendanceHelper';

const monthDefault = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`; };
const monthLabel = (m) => { const [y, mo] = m.split('-'); return new Date(+y, +mo - 1, 1).toLocaleString('en-PK', { month: 'long', year: 'numeric' }); };

const Attendance = () => {
  const { activeCompany, currentUser } = useAuth();
  const { canEdit } = usePagePermissions('salary', 'attendance');

  const [month, setMonth] = useState(monthDefault());
  const [tab, setTab] = useState('sheet');
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [logs, setLogs] = useState([]);
  const [adjustments, setAdjustments] = useState({});
  const [settings, setSettings] = useState(null);
  const [devices, setDevices] = useState([]);
  const [cell, setCell] = useState(null);     // { staffId, date }
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState(null);

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const [st, lg, adj, set, dev] = await Promise.all([
        getDocs(query(collection(db, 'companies', activeCompany.id, 'salaryStaff'), orderBy('staffCode'))),
        fetchMonthLogs(activeCompany.id, month),
        fetchMonthAdjustments(activeCompany.id, month),
        loadAttendanceSettings(activeCompany.id),
        getDocs(collection(db, 'companies', activeCompany.id, 'attendanceDevices')),
      ]);
      setStaff(st.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.isActive !== false));
      setLogs(lg);
      setAdjustments(adj);
      setSettings(set);
      setDevices(dev.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      setAlert({ type: 'error', message: e.message });
    } finally { setLoading(false); }
  }, [activeCompany?.id, month]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    if (!settings) return null;
    return buildMonthlySummary({ month, logs, staff, settings, adjustments });
  }, [month, logs, staff, settings, adjustments]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter(s => (s.fullName || '').toLowerCase().includes(q) || (s.staffCode || '').toLowerCase().includes(q) || String(s.deviceUserId || '').includes(q));
  }, [staff, search]);

  // ─── Mapping ───
  const saveMapping = async (staffId, value) => {
    if (!canEdit) return;
    const v = String(value || '').trim();
    const clash = staff.find(s => s.id !== staffId && String(s.deviceUserId || '').trim() === v && v !== '');
    if (clash) { alert(`Machine ID ${v} is already mapped to ${clash.fullName}.`); return; }
    try {
      await updateDoc(doc(db, 'companies', activeCompany.id, 'salaryStaff', staffId), { deviceUserId: v || null });
      setStaff(prev => prev.map(s => s.id === staffId ? { ...s, deviceUserId: v } : s));
    } catch (e) { setAlert({ type: 'error', message: e.message }); }
  };

  // ─── Overrides ───
  const setOverride = async (staffId, date, status) => {
    if (!canEdit) return;
    const id = `${staffId}_${date}`;
    try {
      if (!status) {
        await deleteDoc(doc(db, 'companies', activeCompany.id, 'attendanceAdjustments', id));
        setAdjustments(prev => { const n = { ...prev }; delete n[id]; return n; });
      } else {
        const data = { staffId, date, month: date.slice(0, 7), status, updatedAt: new Date().toISOString(), updatedBy: currentUser?.uid || null };
        await setDoc(doc(db, 'companies', activeCompany.id, 'attendanceAdjustments', id), data);
        setAdjustments(prev => ({ ...prev, [id]: { id, ...data } }));
      }
    } catch (e) { setAlert({ type: 'error', message: e.message }); }
  };

  // ─── Export CSV of summary ───
  const exportCsv = () => {
    if (!summary) return;
    const rows = [['Staff Code', 'Name', 'Machine ID', 'Working Days', 'Present', 'Late', 'Half Days', 'Absent', 'Paid Leave', 'Late→Absent', 'Hours', 'Payroll Present', 'Payroll Absent', 'Payroll Paid Leave']];
    staff.forEach(s => {
      const r = summary.byStaff[s.id]; if (!r) return;
      rows.push([s.staffCode, s.fullName, s.deviceUserId || '', r.payroll.totalWorkingDays, r.counts.present, r.counts.late, r.counts.half, r.counts.absent, r.counts.paidLeave, r.counts.lateAbsents, fmtHM(minutesOf(r.counts.totalMinutes, r.counts.totalHours)), r.payroll.present, r.payroll.absent, r.payroll.paidLeave]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `attendance-${month}.csv`; a.click();
  };

  const activeDevice = devices.find(d => d.isActive);
  const lastSync = activeDevice?.lastSyncAt?.toDate ? activeDevice.lastSyncAt.toDate() : null;
  const syncStale = lastSync ? (Date.now() - lastSync.getTime()) > 30 * 60 * 1000 : true;
  const unmappedCount = summary ? Object.keys(summary.unmapped).length : 0;
  const cellInfo = cell && summary ? summary.byStaff[cell.staffId]?.days[cell.date] : null;
  const cellStaff = cell ? staff.find(s => s.id === cell.staffId) : null;

  return (
    <PageLayout pageTitle="Attendance">
      <Wrap>
        <TopBar>
          <div>
            <H1>Attendance</H1>
            <Sub>
              {activeDevice ? (
                <><Dot $ok={!syncStale} /> {activeDevice.name}: last sync {lastSync ? lastSync.toLocaleString('en-PK', { dateStyle: 'medium', timeStyle: 'short' }) : 'never'}</>
              ) : <><Dot /> No active device — create a Device Key in Attendance Settings.</>}
            </Sub>
          </div>
          <Controls>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} />
            <input placeholder="Search staff…" value={search} onChange={e => setSearch(e.target.value)} />
            <Btn onClick={load} title="Refresh"><FiRefreshCw /></Btn>
            <Btn onClick={exportCsv} title="Export CSV"><FiDownload /></Btn>
          </Controls>
        </TopBar>

        {alert && <Alert $type={alert.type}><FiInfo /> {alert.message} <span style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={() => setAlert(null)}><FiX /></span></Alert>}

        <Tabs>
          <Tab $on={tab === 'sheet'} onClick={() => setTab('sheet')}>Monthly Sheet</Tab>
          <Tab $on={tab === 'summary'} onClick={() => setTab('summary')}>Summary</Tab>
          <Tab $on={tab === 'mapping'} onClick={() => setTab('mapping')}>Machine ID Mapping {unmappedCount > 0 && <Badge>{unmappedCount}</Badge>}</Tab>
        </Tabs>

        {loading || !summary ? <Loading>Loading {monthLabel(month)}…</Loading> : (
          <>
            {tab === 'sheet' && (
              <>
                <Legend>
                  {['P', 'L', 'H', 'A', 'PL', 'O'].map(k => <LegendItem key={k}><Sw $bg={STATUS_META[k].bg} $c={STATUS_META[k].color}>{k}</Sw>{STATUS_META[k].label}</LegendItem>)}
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: colors.text.secondary }}>Click a day to see punches / override.</span>
                </Legend>
                <Scroll>
                  <Grid>
                    <thead>
                      <tr>
                        <th className="sticky">Staff</th>
                        {summary.dates.map(d => {
                          const wd = weekdayOf(d);
                          return <th key={d} className={settings.weeklyOffDays.includes(wd) ? 'off' : ''}><div>{d.slice(-2)}</div><small>{WEEKDAY_NAMES[wd][0]}</small></th>;
                        })}
                        <th>P</th><th>L</th><th>A</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStaff.map(s => {
                        const r = summary.byStaff[s.id];
                        return (
                          <tr key={s.id}>
                            <td className="sticky"><b>{s.fullName}</b><small>{s.deviceUserId ? `#${s.deviceUserId}` : <Warn>not mapped</Warn>}</small></td>
                            {summary.dates.map(d => {
                              const day = r.days[d];
                              const m = STATUS_META[day.status] || STATUS_META['-'];
                              return (
                                <td key={d} onClick={() => setCell({ staffId: s.id, date: d })}
                                  title={`${d}  In ${fmt12(day.inTime)}  Out ${fmt12(day.outTime)}${day.adjusted ? '  (manual)' : ''}`}>
                                  <Sw $bg={m.bg} $c={m.color} $adj={day.adjusted}>{day.status}</Sw>
                                </td>
                              );
                            })}
                            <td><b>{r.counts.present + r.counts.half * 0.5}</b></td>
                            <td>{r.counts.late}</td>
                            <td>{r.counts.absent}</td>
                          </tr>
                        );
                      })}
                      {filteredStaff.length === 0 && <tr><td colSpan={summary.dates.length + 4} style={{ padding: 30, textAlign: 'center' }}>No active Salary Staff. Add staff under Salary → Salary Staff.</td></tr>}
                    </tbody>
                  </Grid>
                </Scroll>
              </>
            )}

            {tab === 'summary' && (
              <Scroll>
                <Tbl>
                  <thead>
                    <tr>
                      <th>Staff</th><th>Machine ID</th><th>Working Days</th><th>Present</th><th>Late</th><th>Half</th><th>Absent</th><th>Paid Leave</th><th>Late→Abs</th><th>Hours</th>
                      <th className="pay">Payroll Present</th><th className="pay">Payroll Absent</th><th className="pay">Payroll Paid Leave</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaff.map(s => {
                      const r = summary.byStaff[s.id];
                      return (
                        <tr key={s.id}>
                          <td><b>{s.fullName}</b><br /><small>{s.staffCode}</small></td>
                          <td>{s.deviceUserId || <Warn>—</Warn>}</td>
                          <td>{r.payroll.totalWorkingDays}</td>
                          <td>{r.counts.present}</td><td>{r.counts.late}</td><td>{r.counts.half}</td><td>{r.counts.absent}</td><td>{r.counts.paidLeave}</td><td>{r.counts.lateAbsents}</td><td style={{ whiteSpace: 'nowrap' }}>{fmtHM(minutesOf(r.counts.totalMinutes, r.counts.totalHours))}</td>
                          <td className="pay"><b>{r.payroll.present}</b></td><td className="pay"><b>{r.payroll.absent}</b></td><td className="pay"><b>{r.payroll.paidLeave}</b></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Tbl>
                <Hint>Payroll columns are what "Pull from Attendance" fills into Payroll Run for {monthLabel(month)}. Absent for days not yet passed is counted only up to today.</Hint>
              </Scroll>
            )}

            {tab === 'mapping' && (
              <TwoCol>
                <Card>
                  <CardTitle><FiLink /> Salary Staff → Machine user ID</CardTitle>
                  <Hint>Enter the ID the person has on the attendance machine (the "Ac-No" / user ID shown in the device software). Saved instantly.</Hint>
                  <Tbl>
                    <thead><tr><th>Staff</th><th>Designation</th><th style={{ width: 140 }}>Machine ID</th></tr></thead>
                    <tbody>
                      {filteredStaff.map(s => (
                        <tr key={s.id}>
                          <td><b>{s.fullName}</b><br /><small>{s.staffCode}</small></td>
                          <td>{s.designation || '—'}</td>
                          <td><MapInput defaultValue={s.deviceUserId || ''} disabled={!canEdit} placeholder="e.g. 135"
                            onBlur={e => { if (e.target.value.trim() !== String(s.deviceUserId || '')) saveMapping(s.id, e.target.value); }}
                            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </Tbl>
                </Card>
                <Card>
                  <CardTitle><FiAlertCircle /> Punches with no staff mapped ({monthLabel(month)})</CardTitle>
                  {unmappedCount === 0 ? <Hint>All punches this month belong to mapped staff.</Hint> : (
                    <Tbl>
                      <thead><tr><th>Machine ID</th><th>Name on machine</th><th>Punches</th><th>Last</th><th>Assign to</th></tr></thead>
                      <tbody>
                        {Object.entries(summary.unmapped).sort((a, b) => Number(a[0]) - Number(b[0])).map(([uid, u]) => (
                          <tr key={uid}>
                            <td><b>{uid}</b></td><td>{u.name || '—'}</td><td>{u.count}</td><td>{u.lastDate}</td>
                            <td>
                              <select disabled={!canEdit} defaultValue="" onChange={e => { if (e.target.value) saveMapping(e.target.value, uid); }}>
                                <option value="">Select staff…</option>
                                {staff.filter(s => !s.deviceUserId).map(s => <option key={s.id} value={s.id}>{s.fullName} ({s.staffCode})</option>)}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Tbl>
                  )}
                </Card>
              </TwoCol>
            )}
          </>
        )}
      </Wrap>

      {cell && cellInfo && (
        <Overlay onClick={() => setCell(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHead>
              <div><b>{cellStaff?.fullName}</b><br /><small>{cell.date} · {WEEKDAY_NAMES[weekdayOf(cell.date)]}</small></div>
              <Btn onClick={() => setCell(null)}><FiX /></Btn>
            </ModalHead>
            <Facts>
              <div><span>Status</span><Sw $bg={STATUS_META[cellInfo.status].bg} $c={STATUS_META[cellInfo.status].color}>{cellInfo.status}</Sw> {STATUS_META[cellInfo.status].label}{cellInfo.adjusted && ' (manual override)'}</div>
              <div><span>In</span>{fmt12(cellInfo.inTime)}</div>
              <div><span>Out</span>{fmt12(cellInfo.outTime)}</div>
              <div><span>Hours</span>{cellInfo.workedHours != null ? fmtHM(minutesOf(cellInfo.workedMinutes, cellInfo.workedHours)) : '—'}</div>
              <div><span>Late by</span>{cellInfo.lateMinutes ? `${cellInfo.lateMinutes} min` : '—'}</div>
              <div><span>OT</span>{cellInfo.otHours ? fmtHM(minutesOf(cellInfo.otMinutes, cellInfo.otHours)) : '—'}</div>
            </Facts>
            {cellInfo.punches.length > 0 && (
              <Punches>
                <b>Machine punches</b>
                {cellInfo.punches.map(p => <span key={p.id}>{fmt12(p.time)}{p.verifyMode ? ` · ${p.verifyMode}` : ''}</span>)}
              </Punches>
            )}
            {canEdit && (
              <>
                <b style={{ fontSize: 13 }}>Override this day</b>
                <OverrideRow>
                  {['P', 'PL', 'H', 'A', 'O'].map(k => (
                    <OvBtn key={k} $bg={STATUS_META[k].bg} $c={STATUS_META[k].color} $on={adjustments[`${cell.staffId}_${cell.date}`]?.status === k}
                      onClick={() => setOverride(cell.staffId, cell.date, k)}>{STATUS_META[k].label}</OvBtn>
                  ))}
                  {cellInfo.adjusted && <OvBtn $bg="#fff" $c="#555" onClick={() => setOverride(cell.staffId, cell.date, null)}>Clear override</OvBtn>}
                </OverrideRow>
              </>
            )}
          </Modal>
        </Overlay>
      )}
    </PageLayout>
  );
};

export default Attendance;

// ─── styles ────────────────────────────────────────────────────────────────
const Wrap = styled.div`padding: 20px 24px; display: flex; flex-direction: column; gap: 14px;`;
const TopBar = styled.div`display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap;`;
const H1 = styled.h1`margin: 0; font-size: 22px; font-weight: 700; color: ${colors.text.primary};`;
const Sub = styled.div`font-size: 13px; color: ${colors.text.secondary}; display: flex; align-items: center; gap: 6px; margin-top: 4px;`;
const Dot = styled.span`width: 10px; height: 10px; border-radius: 50%; display: inline-block; background: ${p => p.$ok ? '#2e7d32' : '#bdbdbd'};`;
const Controls = styled.div`display: flex; gap: 8px; align-items: center; input { padding: 8px 10px; border: 1px solid #ddd; border-radius: 6px; font-family: inherit; font-size: 13px; }`;
const Btn = styled.button`display: inline-flex; align-items: center; justify-content: center; background: #fff; border: 1px solid #ddd; border-radius: 6px; padding: 8px 10px; cursor: pointer; color: ${colors.text.primary}; &:hover { border-color: #800020; color: #800020; }`;
const Alert = styled.div`display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; font-size: 14px;
  background: ${p => p.$type === 'error' ? '#fdecea' : '#e8f5e9'}; color: ${p => p.$type === 'error' ? '#b71c1c' : '#1b5e20'};`;
const Tabs = styled.div`display: flex; gap: 4px; border-bottom: 1px solid #e5e5e5;`;
const Tab = styled.button`background: none; border: none; border-bottom: 2px solid ${p => p.$on ? '#800020' : 'transparent'}; color: ${p => p.$on ? '#800020' : colors.text.secondary}; font-weight: 600; padding: 10px 14px; cursor: pointer; font-size: 14px; display: inline-flex; align-items: center; gap: 6px;`;
const Badge = styled.span`background: #c62828; color: #fff; border-radius: 10px; font-size: 11px; padding: 1px 7px;`;
const Legend = styled.div`display: flex; gap: 14px; align-items: center; flex-wrap: wrap; font-size: 12px; color: ${colors.text.secondary};`;
const LegendItem = styled.span`display: inline-flex; align-items: center; gap: 5px;`;
const Sw = styled.span`display: inline-flex; align-items: center; justify-content: center; min-width: 24px; height: 22px; padding: 0 4px; border-radius: 5px; font-size: 11px; font-weight: 700;
  background: ${p => p.$bg}; color: ${p => p.$c}; outline: ${p => p.$adj ? '2px solid #1565c0' : 'none'}; outline-offset: -2px;`;
const Scroll = styled.div`overflow-x: auto; background: #fff; border: 1px solid #eee; border-radius: 10px;`;
const Grid = styled.table`border-collapse: separate; border-spacing: 0; font-size: 12px; min-width: 100%;
  th, td { padding: 4px 3px; text-align: center; border-bottom: 1px solid #f0f0f0; white-space: nowrap; }
  th { font-size: 11px; color: ${colors.text.secondary}; background: #fafafa; position: sticky; top: 0; small { display: block; font-size: 9px; } }
  th.off { color: #c62828; }
  .sticky { position: sticky; left: 0; background: #fff; text-align: left; z-index: 2; min-width: 160px; padding-left: 10px; box-shadow: 2px 0 0 #f0f0f0; }
  th.sticky { z-index: 3; background: #fafafa; }
  td.sticky small { display: block; color: ${colors.text.secondary}; font-size: 10px; }
  td:not(.sticky) { cursor: pointer; } td:hover ${Sw} { filter: brightness(0.92); }`;
const Warn = styled.span`color: #ef6c00;`;
const Tbl = styled.table`width: 100%; border-collapse: collapse; font-size: 13px; background: #fff;
  th { text-align: left; font-size: 11px; text-transform: uppercase; color: ${colors.text.secondary}; padding: 10px 8px; border-bottom: 1px solid #eee; background: #fafafa; }
  td { padding: 8px; border-bottom: 1px solid #f3f3f3; vertical-align: middle; } small { color: ${colors.text.secondary}; }
  .pay { background: #fff8e1; } select { padding: 6px 8px; border: 1px solid #ddd; border-radius: 6px; font-family: inherit; font-size: 13px; max-width: 220px; }`;
const Hint = styled.div`font-size: 12px; color: ${colors.text.secondary}; padding: 10px 12px;`;
const TwoCol = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 14px; @media (max-width: 1000px) { grid-template-columns: 1fr; }`;
const Card = styled.div`background: #fff; border: 1px solid #eee; border-radius: 10px; overflow: hidden;`;
const CardTitle = styled.div`display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 700; padding: 14px 14px 4px;`;
const MapInput = styled.input`width: 100%; padding: 7px 10px; border: 1px solid #ddd; border-radius: 6px; font-family: inherit; font-size: 13px; &:focus { border-color: #800020; outline: none; }`;
const Loading = styled.div`padding: 40px; text-align: center; color: ${colors.text.secondary};`;
const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000;`;
const Modal = styled.div`background: #fff; border-radius: 12px; padding: 18px 20px; width: min(460px, 92vw); display: flex; flex-direction: column; gap: 12px;`;
const ModalHead = styled.div`display: flex; justify-content: space-between; align-items: flex-start; small { color: ${colors.text.secondary}; }`;
const Facts = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 6px 14px; font-size: 13px; div { display: flex; align-items: center; gap: 6px; } span:first-child { color: ${colors.text.secondary}; min-width: 56px; font-size: 11px; text-transform: uppercase; }`;
const Punches = styled.div`display: flex; flex-wrap: wrap; gap: 6px; font-size: 12px; align-items: center; b { width: 100%; font-size: 13px; } span { background: #f5f5f5; border-radius: 4px; padding: 3px 7px; }`;
const OverrideRow = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const OvBtn = styled.button`border: 1px solid ${p => p.$on ? p.$c : '#ddd'}; background: ${p => p.$bg}; color: ${p => p.$c}; border-radius: 6px; padding: 6px 10px; font-size: 12px; font-weight: 600; cursor: pointer; box-shadow: ${p => p.$on ? `inset 0 0 0 1px ${p.$c}` : 'none'};`;
